import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useNavigateWithParams } from '@/hooks/useNavigateWithParams';
import { NAV_ITEMS } from '@/utils/constants';

export interface NavItem {
  id: string;
  labelKey: string;
  path: string;
  icon: string; // SVG icon import
}

/**
 * AppMenu - Bottom navigation bar component
 *
 * 5 main navigation items for the app:
 * - Home, Mappa, Stanze, Quiz, Info
 *
 * Features:
 * - Active state based on current route
 * - Icon highlighted with primary color when active
 * - Keyboard accessible (Tab, Enter)
 * - Mobile-first responsive
 * - Touch target: 44x44px minimum
 *
 * Accessibility:
 * - Semantic <nav> element
 * - ARIA labels for screen readers
 * - Clear focus indicators
 */
export function 
AppMenu() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigateWithParams();

  const [announcement, setAnnouncement] = useState('');
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const activeItem = NAV_ITEMS.find(item => item.path === location.pathname);
    if (activeItem) {
      setAnnouncement('');
      const timer = setTimeout(() => {
        setAnnouncement(t('nav.navigatedTo', { page: t(activeItem.labelKey) }));
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, t]);

  return (
    <nav
      className="w-full bg-white border-t border-border flex pb-4"
      aria-label={t('nav.ariaLabel', 'App Navigation')}
    >
      <span className="sr-only" aria-live="polite" aria-atomic="true">{announcement}</span>
      <ul className="flex flex-row w-full justify-around items-stretch list-none p-0 m-0 gap-0 overflow-x-auto">
        {NAV_ITEMS.map((item) => {
          const isActive = location.pathname === item.path;

          const Tag = isActive ? 'span' : 'a';

          return (
            <li
              key={item.id}
              className="flex-1 flex items-stretch justify-stretch min-h-16"
            >
              <Tag
                {...(!isActive && {
                  href: item.path,
                  onClick: (e: React.MouseEvent) => { e.preventDefault(); navigate(item.path); },
                  'aria-label': t(item.labelKey),
                })}
                tabIndex={0}
                className={`flex-1 flex flex-col items-center justify-center gap-1 py-1 bg-transparent border-none text-label font-medium transition-colors duration-200 relative min-w-11 no-underline focus-visible:outline-2 focus-visible:outline-primary focus-visible:-outline-offset-1 ${
                  isActive ? 'cursor-default' : 'cursor-pointer hover:bg-opacity-30 px-4'
                }`}
                aria-current={isActive ? 'page' : undefined}
              >
                {/* Icon with oval background — always hidden from AT */}
                <div
                  aria-hidden="true"
                  className={`flex items-center justify-center rounded-lg transition-colors duration-200 py-1 ${
                    isActive ? 'bg-surface px-3' : ''
                  }`}
                >
                  <img src={item.icon} alt="" className="w-6 h-6" />
                </div>
                {/* Label: hidden from AT on <a> (aria-label covers it), exposed when active <span> (serves as accessible name) */}
                <span aria-hidden={isActive ? undefined : 'true'} className="block whitespace-nowrap text-ellipsis overflow-hidden max-w-full text-xs text-[#625B71] transition-colors duration-200">
                  {t(item.labelKey)}
                </span>
              </Tag>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
